import { useState } from "react";
import { api } from "../../../helpers/api";
import { useQuery } from "@tanstack/react-query";
import { formatNumber, getUser } from "../../../helpers/utils";
import { RevenueChartFilterSelect } from "./RevenueChartFilterSelect";
import {
  LineChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Line,
  Tooltip,
} from "recharts";
import moment from "moment";
import { months } from "../../users/users-details/dashboard/components/RevenueSummary/utils";
import { Panel } from "../../../components/Panel";
import { ReactComponent as UpIcon } from "../../../assets/icons/up-icon.svg";

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="flex flex-col gap-1 items-center justify-center px-4 py-5 !shadow-dropdown rounded-[11px] bg-surface-primary">
        <p className="leading-[18px] text-base text-content-primary">
          {payload[0]?.payload?.day
            ? moment(payload[0]?.payload?.day).format("MMM DD, YYYY")
            : moment()
                .month(payload[0]?.payload?.month - 1)
                .format("MMM, YYYY")}
        </p>
        <p className="text-[22px] font-bold leading-[24px] text-content-primary">
          ${payload[0].value}
        </p>
      </div>
    );
  }

  return null;
};

function CustomizedLabel({ x, y, payload, ...rest }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={6} y={0} dy={16} textAnchor="end" fill="#666">
        {rest.type === "CURRENT_WEEK"
          ? moment(payload.value).format("ddd")
          : rest.type === "CURRENT_MONTH"
          ? moment(payload.value).format("DD")
          : // turn month number to month name
          payload.value >= 1 && payload.value <= 12
          ? months[payload.value - 1]
          : payload.value}
      </text>
    </g>
  );
}

export function RevenueChart() {
  const [duration, setDuration] = useState<
    "CURRENT_WEEK" | "CURRENT_MONTH" | "CURRENT_YEAR"
  >("CURRENT_WEEK");
  const user = getUser();

  const { data, isLoading } = useQuery({
    queryKey: ["chart", user?.id, duration],
    queryFn: () =>
      api.getDashboardCharts({
        id: user!.id,
        type: "REVENUE",
        duration: duration,
      }),
    enabled: !!user,
  });

  const hasDay = !!data?.revenue?.[0]?.day;
  return (
    <Panel
      className="flex-1"
      label="Total Revenue"
      actions={
        <RevenueChartFilterSelect
          value={duration}
          onChange={(value) => setDuration(value)}
        />
      }
      contentClassName="px-4 py-6"
    >
      <div className="flex flex-row justify-between rounded-[8px] border border-border-secondary p-3 mb-6">
        <span className="text-content-primary">
          {hasDay
            ? moment(data?.revenue?.[0]?.day).format("MMM DD")
            : moment(data?.revenue?.[0]?.month, "MM")
                .startOf("year")
                .format("MMM DD")}{" "}
          -{" "}
          {hasDay
            ? moment(data?.revenue?.[data?.revenue?.length - 1].day).format(
                "MMM DD"
              )
            : moment(data?.revenue?.[data?.revenue?.length - 1].month, "MM")
                .endOf("year")
                .format("MMM DD")}
        </span>

        <div className="flex flex-row gap-1 items-center">
          <span className="text-content-primary">
            $
            {data?.revenue?.reduce(
              (acc: number, curr: any) => acc + curr.count,
              0
            )}
          </span>
          <div className="w-5 h-5 flex justify-center items-center">
            <UpIcon className="w-5 h-5" />
          </div>
        </div>
      </div>
      <div className="h-[calc(100vh-380px)]">
        <ResponsiveContainer width="100%" height={"100%"}>
          <LineChart
            data={
              (data?.revenue || []).map((item) => ({
                ...item,
                name: item.day || item.month,
              })) || []
            }
            className="!cursor-pointer"
            margin={{
              top: 5,
              right: 20,
              left: 5,
              bottom: 5,
            }}
          >
            <defs>
              <linearGradient
                id="paint0_linear_53_758"
                x1="0"
                y1="0" // Start from the bottom (100%)
                x2="0"
                y2="100%" // End at the top (0)
                gradientUnits="userSpaceOnUse"
              >
                <stop stop-color="rgba(0, 0, 0, 0.15)" />
                <stop
                  offset="1"
                  stop-color="rgba(0, 0, 0, 0)"
                  stop-opacity="1"
                />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="name"
              tick={(props) => <CustomizedLabel {...props} type={duration} />}
              interval={0}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              className="!text-2xs !text-content-tertiary leading-[11px]"
              width={20}
              tickFormatter={(value) => {
                // make value to k denotes thousands
                const val = Number(value);
                // If zero then show 0 without dollar sign
                if (val === 0) {
                  return "0";
                }
                if (val > 1000) {
                  return `${formatNumber(Number(val || 0), "$")}`;
                }
                return `$${val}`;
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dot={false}
              strokeWidth={2}
              dataKey="count"
              stroke="#000"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
}
